import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Flex,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';

const FALLBACK_COLORS = {
    cta: '#11047A',
    highlight: '#F7FAFC',
    accent: '#2D3748',
    buttonText: '#FFFFFF',
};

const sanitizeColor = (value, fallbackColor) => {
    if (typeof value !== 'string' || !value.trim()) {
        return fallbackColor;
    }

    const nextColor = value.trim();

    if (typeof window !== 'undefined' && window.CSS?.supports?.('color', nextColor)) {
        return nextColor;
    }

    return fallbackColor;
};

/**
 * @typedef {Object} ThemePreset
 * @property {string} value
 * @property {string} label
 * @property {string} description
 * @property {string} cta_color
 * @property {string} highlight_color
 * @property {string} accent_color
 * @property {string} button_text_color
 */

/**
 * @param {{
 *   presets: ThemePreset[],
 *   value: string,
 *   onChange: (nextValue: string) => void,
 *   columns?: Object,
 *   isLoading?: boolean,
 *   errorMessage?: string,
 *   emptyMessage?: string
 * }} props
 */
const ThemePresetPicker = ({
    presets,
    value,
    onChange,
    columns = { base: 1, md: 2, xl: 3 },
    isLoading = false,
    errorMessage = '',
    emptyMessage = 'No theme presets are available for this form.',
}) => {
    if (isLoading) {
        return (
            <Flex
                minH="180px"
                align="center"
                justify="center"
                direction="column"
                gap="3"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.200"
                borderRadius="xl"
                bg="gray.50"
                px="6"
                py="10"
            >
                <Spinner color="blue.500" thickness="3px" />
                <Text fontSize="sm" color="gray.500">
                    Loading available theme presets...
                </Text>
            </Flex>
        );
    }

    if (errorMessage) {
        return (
            <Alert status="error" borderRadius="xl" alignItems="flex-start">
                <AlertIcon mt="1" />
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    if (!presets.length) {
        return (
            <Alert status="warning" borderRadius="xl" alignItems="flex-start">
                <AlertIcon mt="1" />
                <AlertDescription>{emptyMessage}</AlertDescription>
            </Alert>
        );
    }

    return (
        <SimpleGrid columns={columns} spacing="4">
            {presets.map((preset) => {
                const isSelected = preset.value === value;
                const ctaColor = sanitizeColor(preset.cta_color, FALLBACK_COLORS.cta);
                const highlightColor = sanitizeColor(preset.highlight_color, FALLBACK_COLORS.highlight);
                const accentColor = sanitizeColor(preset.accent_color, FALLBACK_COLORS.accent);
                const buttonTextColor = sanitizeColor(
                    preset.button_text_color,
                    FALLBACK_COLORS.buttonText,
                );

                return (
                    <Box
                        as="button"
                        type="button"
                        key={preset.value}
                        onClick={() => onChange(preset.value)}
                        aria-pressed={isSelected}
                        textAlign="left"
                        w="100%"
                        h="100%"
                        borderWidth="2px"
                        borderColor={isSelected ? 'blue.500' : 'gray.200'}
                        borderRadius="xl"
                        bg="white"
                        p="4"
                        boxShadow={isSelected ? '0 0 0 1px #3182CE, 0 12px 24px rgba(49, 130, 206, 0.18)' : 'sm'}
                        transition="all 0.2s ease"
                        _hover={{
                            borderColor: isSelected ? 'blue.500' : 'gray.300',
                            transform: 'translateY(-2px)',
                            boxShadow: 'md',
                        }}
                        _focusVisible={{
                            outline: 'none',
                            boxShadow: '0 0 0 3px rgba(49, 130, 206, 0.35)',
                        }}
                    >
                        <Flex justify="space-between" align="flex-start" gap="3">
                            <Stack spacing="1" flex="1">
                                <Text fontSize="md" fontWeight="bold" color="gray.800">
                                    {preset.label}
                                </Text>
                                <Text fontSize="sm" color="gray.500" noOfLines={2}>
                                    {preset.description || 'No description provided.'}
                                </Text>
                            </Stack>
                            <CheckCircleIcon
                                color={isSelected ? 'blue.500' : 'gray.300'}
                                boxSize="5"
                                flexShrink="0"
                            />
                        </Flex>

                        <Box
                            mt="3"
                            p="2.5"
                            borderRadius="lg"
                            borderWidth="1px"
                            borderColor="gray.200"
                            bg="gray.50"
                            overflow="hidden"
                        >
                            <Flex align="center" gap="1.5" mb="2.5">
                                <Box w="3" h="3" borderRadius="full" bg={ctaColor} />
                                <Box w="3" h="3" borderRadius="full" bg={highlightColor} borderWidth="1px" borderColor="gray.200" />
                                <Box w="3" h="3" borderRadius="full" bg={accentColor} />
                                <Box
                                    w="7"
                                    h="3"
                                    borderRadius="full"
                                    bg={buttonTextColor}
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    opacity="0.95"
                                />
                            </Flex>

                            <Box
                                borderWidth="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                bg="white"
                                p="2.5"
                            >
                                <Stack spacing="2">
                                    <Box w="30%" h="2.5" borderRadius="full" bg="gray.700" opacity="0.12" />
                                    <Box w="58%" h="2" borderRadius="full" bg="gray.600" opacity="0.1" />

                                    <SimpleGrid columns={2} spacing="2">
                                        <Box
                                            borderWidth="2px"
                                            borderColor={ctaColor}
                                            borderRadius="md"
                                            bg={highlightColor}
                                            p="2"
                                        >
                                            <Box w="55%" h="2" borderRadius="full" bg="gray.700" opacity="0.14" />
                                            <Box
                                                mt="2"
                                                w="28px"
                                                h="10px"
                                                borderRadius="full"
                                                bg={accentColor}
                                            />
                                            <Box
                                                mt="2"
                                                ml="auto"
                                                w="26%"
                                                h="2.5"
                                                borderRadius="full"
                                                bg={accentColor}
                                                opacity="0.9"
                                            />
                                        </Box>

                                        <Box
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                            bg="white"
                                            p="2"
                                        >
                                            <Box w="60%" h="2" borderRadius="full" bg="gray.600" opacity="0.12" />
                                            <Box
                                                mt="4"
                                                ml="auto"
                                                w="24%"
                                                h="2.5"
                                                borderRadius="full"
                                                bg="gray.600"
                                                opacity="0.12"
                                            />
                                        </Box>
                                    </SimpleGrid>

                                    <Box
                                        borderWidth="2px"
                                        borderColor={ctaColor}
                                        borderRadius="md"
                                        bg={highlightColor}
                                        px="2"
                                        py="2"
                                    >
                                        <Flex justify="space-between" align="center" gap="2">
                                            <Box w="36%" h="2" borderRadius="full" bg="gray.700" opacity="0.14" />
                                            <Box w="18%" h="2.5" borderRadius="full" bg={accentColor} opacity="0.9" />
                                        </Flex>
                                    </Box>

                                    <Box pt="1">
                                        <Box w="34%" h="2.5" borderRadius="full" bg="gray.700" opacity="0.12" mb="2" />
                                        <Stack spacing="1.5">
                                            <Flex justify="space-between" align="center">
                                                <Box w="32%" h="2" borderRadius="full" bg="gray.600" opacity="0.12" />
                                                <Box w="16%" h="2" borderRadius="full" bg={accentColor} opacity="0.8" />
                                            </Flex>
                                            <Flex justify="space-between" align="center">
                                                <Box w="24%" h="2" borderRadius="full" bg="gray.600" opacity="0.1" />
                                                <Box w="12%" h="2" borderRadius="full" bg="gray.500" opacity="0.18" />
                                            </Flex>
                                            <Flex justify="space-between" align="center" pt="1" borderTopWidth="1px" borderColor="gray.100">
                                                <Box w="28%" h="2.5" borderRadius="full" bg="gray.700" opacity="0.15" />
                                                <Box w="20%" h="2.5" borderRadius="full" bg={accentColor} opacity="0.95" />
                                            </Flex>
                                        </Stack>
                                    </Box>

                                    <Flex align="center" gap="2" pt="1">
                                        <Box
                                            w="14px"
                                            h="14px"
                                            borderRadius="sm"
                                            bg={ctaColor}
                                        />
                                        <Box w="55%" h="2" borderRadius="full" bg="gray.600" opacity="0.12" />
                                        <Box w="18%" h="2" borderRadius="full" bg={accentColor} opacity="0.75" />
                                    </Flex>

                                    <Flex
                                        minH="30px"
                                        borderRadius="md"
                                        bg={ctaColor}
                                        align="center"
                                        justify="center"
                                    >
                                        <Box
                                            w="42%"
                                            h="2.5"
                                            borderRadius="full"
                                            bg={buttonTextColor}
                                            opacity="0.92"
                                        />
                                    </Flex>
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </SimpleGrid>
    );
};

ThemePresetPicker.propTypes = {
    presets: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        description: PropTypes.string,
        cta_color: PropTypes.string,
        highlight_color: PropTypes.string,
        accent_color: PropTypes.string,
        button_text_color: PropTypes.string,
    })),
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    columns: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.object,
    ]),
    isLoading: PropTypes.bool,
    errorMessage: PropTypes.string,
    emptyMessage: PropTypes.string,
};

ThemePresetPicker.defaultProps = {
    presets: [],
    value: '',
    columns: { base: 1, md: 2, xl: 3 },
    isLoading: false,
    errorMessage: '',
    emptyMessage: 'No theme presets are available for this form.',
};

export default ThemePresetPicker;
